const SKIP_TAGS = new Set(['script', 'style', 'noscript', 'template', 'head', 'meta', 'link', 'title'])

const TAG_ICONS = {
  div: 'pi pi-box',
  section: 'pi pi-th-large',
  article: 'pi pi-file',
  main: 'pi pi-home',
  header: 'pi pi-arrow-up',
  footer: 'pi pi-arrow-down',
  nav: 'pi pi-bars',
  h1: 'pi pi-heading',
  h2: 'pi pi-heading',
  h3: 'pi pi-heading',
  h4: 'pi pi-heading',
  h5: 'pi pi-heading',
  h6: 'pi pi-heading',
  p: 'pi pi-align-left',
  span: 'pi pi-minus',
  a: 'pi pi-link',
  img: 'pi pi-image',
  ul: 'pi pi-list',
  ol: 'pi pi-list',
  li: 'pi pi-circle',
  table: 'pi pi-table',
  tr: 'pi pi-minus',
  td: 'pi pi-stop',
  th: 'pi pi-stop',
  form: 'pi pi-inbox',
  input: 'pi pi-pencil',
  button: 'pi pi-stop',
  blockquote: 'pi pi-comment',
}

let nodeIdCounter = 0

export function resetNodeIdCounter() {
  nodeIdCounter = 0
}

export function assignNodeIds(root, preserveExisting = false) {
  if (!preserveExisting) resetNodeIdCounter()
  if (!root) return
  const walk = (el) => {
    if (!el || el.nodeType !== 1) return
    const tag = String(el.tagName || '').toLowerCase()
    if (SKIP_TAGS.has(tag)) return
    const existingId = el.getAttribute('data-ncht-node-id')
    if (preserveExisting && existingId) {
      const numeric = Number.parseInt(existingId, 10)
      if (Number.isFinite(numeric) && numeric > nodeIdCounter) nodeIdCounter = numeric
    } else {
      nodeIdCounter += 1
      el.setAttribute('data-ncht-node-id', String(nodeIdCounter))
    }
    Array.from(el.children || []).forEach(walk)
  }
  walk(root)
}

function getDirectTextPreview(el, maxLen = 28) {
  let text = ''
  for (const child of el.childNodes || []) {
    if (child.nodeType === 3) {
      text += String(child.nodeValue || '')
    }
  }
  text = text.trim().replace(/\s+/g, ' ')
  if (!text) return ''
  if (text.length <= maxLen) return `"${text}"`
  return `"${text.slice(0, maxLen)}…"`
}

function buildNodeLabel(el) {
  const tag = String(el.tagName || '').toLowerCase()
  const parts = [tag]
  const id = el.getAttribute('id')
  if (id) parts.push(`#${id}`)
  const cls = Array.from(el.classList || []).filter((c) => !c.startsWith('ncht-'))[0]
  if (cls) parts.push(`.${cls}`)
  const preview = getDirectTextPreview(el)
  if (preview) parts.push(preview)
  return parts.join(' ')
}

function elementToTreeNode(el) {
  const tag = String(el.tagName || '').toLowerCase()
  if (SKIP_TAGS.has(tag)) return null
  const nodeId = el.getAttribute('data-ncht-node-id')
  if (!nodeId) return null

  const children = []
  for (const child of el.children || []) {
    const childNode = elementToTreeNode(child)
    if (childNode) children.push(childNode)
  }

  return {
    key: String(nodeId),
    label: buildNodeLabel(el),
    icon: TAG_ICONS[tag] || 'pi pi-circle',
    selectable: true,
    children: children.length ? children : undefined,
  }
}

export function buildDomTreeFromDocument(doc, preserveExistingIds = false) {
  if (!doc?.body) return []
  assignNodeIds(doc.body, preserveExistingIds)
  const nodes = []
  for (const child of doc.body.children || []) {
    const node = elementToTreeNode(child)
    if (node) nodes.push(node)
  }
  return nodes
}

export function findElementByNodeId(doc, nodeId) {
  if (!doc || !nodeId) return null
  return doc.querySelector(`[data-ncht-node-id="${nodeId}"]`)
}

export function getParentNodeIds(el) {
  const ids = []
  let node = el?.parentElement
  while (node && node.nodeType === 1) {
    const tag = String(node.tagName || '').toLowerCase()
    if (tag === 'html') break
    const id = node.getAttribute('data-ncht-node-id')
    if (id) ids.push(id)
    node = node.parentElement
  }
  return ids.reverse()
}

export function collectExpandedKeys(nodeIds) {
  const expanded = {}
  for (const id of nodeIds) {
    expanded[id] = true
  }
  return expanded
}

const STRUCTURE_BLOCKED_TAGS = new Set(['html', 'body', 'head', 'script', 'style', 'noscript', 'template'])
const VOID_TAGS = new Set(['img', 'input', 'br', 'hr', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'])

export function isInvalidStructureTarget(el) {
  if (!el || el.nodeType !== 1) return true
  const tag = String(el.tagName || '').toLowerCase()
  return STRUCTURE_BLOCKED_TAGS.has(tag) || VOID_TAGS.has(tag)
}

function isValidStructureParent(el) {
  if (!el || el.nodeType !== 1) return false
  const tag = String(el.tagName || '').toLowerCase()
  if (tag === 'body') return true
  return !STRUCTURE_BLOCKED_TAGS.has(tag) && !VOID_TAGS.has(tag)
}

export function isDescendantOf(ancestor, node) {
  if (!ancestor || !node) return false
  let current = node
  while (current) {
    if (current === ancestor) return true
    current = current.parentElement
  }
  return false
}

export function stripNodeIds(el) {
  if (!el || el.nodeType !== 1) return
  el.removeAttribute('data-ncht-node-id')
  el.querySelectorAll('[data-ncht-node-id]').forEach((n) => n.removeAttribute('data-ncht-node-id'))
}

export function duplicateElementByNodeId(doc, nodeId) {
  const el = findElementByNodeId(doc, nodeId)
  if (!el || isInvalidStructureTarget(el)) return null
  const clone = el.cloneNode(true)
  stripNodeIds(clone)
  el.insertAdjacentElement('afterend', clone)
  return clone
}

function resolveDropMode(event, dragNode, dropNode) {
  const dropPosition = event?.dropPosition
  if (dropPosition === 0) return 'child'
  if (dropPosition === -1) return 'before'
  if (dropPosition === 1) return 'after'

  const index = event?.index
  if (index == null || index < 0) return 'child'
  if (!dropNode?.children?.length) return 'child'
  const childAtIndex = dropNode.children[index]
  return childAtIndex?.key === dragNode?.key ? 'child' : 'before'
}

export function moveElementByTreeDrop(doc, event) {
  const dragNode = event?.dragNode
  const dropNode = event?.dropNode
  if (!doc || !dragNode?.key || !dropNode?.key) return null

  const dragEl = findElementByNodeId(doc, dragNode.key)
  const dropEl = findElementByNodeId(doc, dropNode.key)
  if (!dragEl || !dropEl) return null
  if (dragEl === dropEl) return null
  if (isInvalidStructureTarget(dragEl) || isInvalidStructureTarget(dropEl)) return null
  if (isDescendantOf(dragEl, dropEl)) return null

  const dropMode = resolveDropMode(event, dragNode, dropNode)

  if (dropMode === 'child') {
    if (VOID_TAGS.has(String(dropEl.tagName || '').toLowerCase())) return null
    dropEl.appendChild(dragEl)
    return dragEl
  }

  const parentEl = dropEl.parentElement
  if (!isValidStructureParent(parentEl)) return null

  const insertBefore = dropMode === 'before' ? dropEl : dropEl.nextElementSibling
  parentEl.insertBefore(dragEl, insertBefore)
  return dragEl
}

export function findTreeNodeByKey(nodes, key) {
  const target = String(key || '')
  if (!target) return null
  const walk = (list) => {
    for (const node of list || []) {
      if (String(node.key) === target) return node
      const found = walk(node.children)
      if (found) return found
    }
    return null
  }
  return walk(nodes)
}

export function isTreeNodeDescendant(ancestor, key) {
  if (!ancestor || !key) return false
  const walk = (node) => {
    if (String(node.key) === String(key)) return true
    for (const child of node.children || []) {
      if (walk(child)) return true
    }
    return false
  }
  return walk(ancestor)
}

export function findTreeNodeKeyFromEventTarget(target) {
  const contentEl = target?.closest?.('.p-tree-node-content')
  if (!contentEl) return null
  const keyEl = contentEl.querySelector('[data-ncht-tree-key]')
  const key = keyEl?.getAttribute?.('data-ncht-tree-key')
  return key ? String(key) : null
}
